import { useState } from 'react';
import { useHayCambios } from '../hooks/useHayCambios';
import { useCierreConfirmado } from '../hooks/useCierreConfirmado';
import { useApi } from '../hooks/useApi';
import { DataTable } from '../components/DataTable';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { HelpTip } from '../components/HelpTip';
import { Confirm } from '../components/Confirm';
import { api, extractErrorMessage } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/Button';

interface Unidad { codigo: string; descripcion: string; abreviatura: string; }

const emptyForm = () => ({ codigo: '', descripcion: '', abreviatura: '' });

export function UnidadesPage() {
  const { data, loading, refetch } = useApi<Unidad[]>('/api/v3/unidades-medida');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const filtered = (data ?? []).filter(row => Object.entries(filters).every(([key, val]) => !val || String((row as any)[key] ?? '').toLowerCase().includes(val.toLowerCase())));
  const [open, setOpen] = useState(false);
  const [editingCodigo, setEditingCodigo] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Unidad | null>(null);
  const [formError, setFormError] = useState('');
  const cerrarForm = () => { setOpen(false); setEditingCodigo(null); setForm(emptyForm()); setFormError(''); };
  const cierreForm = useCierreConfirmado(useHayCambios(form, open), cerrarForm);
  const toast = useToast();
  const { can } = useAuth();

  const handleSave = async () => {
    if (!form.codigo.trim() || !form.descripcion.trim() || !form.abreviatura.trim()) {
      setFormError('Todos los campos son obligatorios');
      toast.addToast('Todos los campos son obligatorios', 'error');
      return;
    }
    setFormError('');
    setSubmitting(true);
    try {
      if (editingCodigo) {
        await api.patch(`/api/v3/unidades-medida/${editingCodigo}`, {
          descripcion: form.descripcion,
          abreviatura: form.abreviatura,
        });
        toast.addToast('Unidad actualizada', 'success');
      } else {
        await api.post('/api/v3/unidades-medida', form);
        toast.addToast('Unidad creada', 'success');
      }
      cerrarForm();
      refetch();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (item: Unidad) => {
    if (!can('unidades', 'editar')) { toast.addToast('No tenés permiso para realizar esta acción', 'error'); return; }
    setForm({ codigo: item.codigo, descripcion: item.descripcion, abreviatura: item.abreviatura });
    setEditingCodigo(item.codigo);
    setFormError('');
    setOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    if (!can('unidades', 'eliminar')) { toast.addToast('No tenés permiso para realizar esta acción', 'error'); return; }
    setSubmitting(true);
    try {
      await api.delete(`/api/v3/unidades-medida/${deleteTarget.codigo}`);
      toast.addToast(`"${deleteTarget.descripcion}" eliminada`, 'success');
      setConfirmDelete(false);
      setDeleteTarget(null);
      refetch();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { key: 'codigo', header: 'Codigo' },
    { key: 'descripcion', header: 'Descripcion' },
    { key: 'abreviatura', header: 'Abrev.' },
    {
      key: 'actions',
      header: '',
      render: (r: Unidad) => (
        <button
          onClick={e => {
            e.stopPropagation();
            setDeleteTarget(r);
            setConfirmDelete(true);
          }}
          disabled={!can('unidades', 'eliminar')}
          className="text-red-500 hover:text-red-700 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          title={!can('unidades', 'eliminar') ? 'No tenés permiso para realizar esta acción' : 'Eliminar unidad'}
        >
          Eliminar
        </button>
      ),
      filterable: false,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 inline">Unidades de Medida</h1>
          <HelpTip tips={['Catálogo de unidades usadas en artículos, compras y remitos', 'Cada unidad tiene código, descripción y abreviatura', 'La UM base por defecto es UN (unidad)']} />
          <p className="text-slate-500 text-sm mt-1">Catalogo de unidades</p>
        </div>
        <Button
          onClick={() => {
            setEditingCodigo(null);
            setForm(emptyForm());
            setFormError('');
            setOpen(true);
          }}
          disabled={!can('unidades', 'crear')}
          title={!can('unidades', 'crear') ? 'No tenés permiso para realizar esta acción' : undefined}
        >
          + Nueva
        </Button>
      </div>
      <DataTable
        columns={columns}
        data={filtered}
        loading={loading}
        onRowClick={handleEdit}
        filters={filters}
        onFilterChange={(k, v) => setFilters(f => v ? { ...f, [k]: v } : Object.fromEntries(Object.entries(f).filter(([key]) => key !== k)))}
      />
      <Modal
        open={open}
        cierre={cierreForm}
        title={editingCodigo ? `Editar Unidad: ${editingCodigo}` : 'Nueva Unidad'}
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Codigo</label>
            <input
              value={form.codigo}
              onChange={e => setForm({ ...form, codigo: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              disabled={!!editingCodigo}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Descripcion</label>
            <input value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Abreviatura</label>
            <input value={form.abreviatura} onChange={e => setForm({ ...form, abreviatura: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
          </div>
          {formError && <p className="text-red-600 text-sm">{formError}</p>}
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={cierreForm.cerrar}
              className="px-4 py-2 text-sm"
            >
              Cancelar
            </button>
            <Button
              onClick={handleSave}
              loading={submitting}
              disabled={editingCodigo ? !can('unidades', 'editar') : !can('unidades', 'crear')}
              title={(editingCodigo ? !can('unidades', 'editar') : !can('unidades', 'crear')) ? 'No tenés permiso para realizar esta acción' : undefined}
            >
              {submitting ? 'Guardando...' : editingCodigo ? 'Guardar' : 'Crear'}
            </Button>
          </div>
        </div>
      </Modal>

      <Confirm
        open={confirmDelete}
        title="Eliminar Unidad de Medida"
        message={`¿Estás seguro de eliminar "${deleteTarget?.descripcion}" (${deleteTarget?.codigo})?`}
        confirmLabel="Eliminar"
        danger
        onConfirm={handleDelete}
        onCancel={() => {
          setConfirmDelete(false);
          setDeleteTarget(null);
        }}
      />
    </div>
  );
}
